import { ARROW_DROP_DOWN_PATH } from "@/components/ui/mui-arrow-icons";
import { cn } from "@/lib/utils";

// MUI's select caret — `ArrowDropDownIcon` in the `.MuiSelect-icon` slot — and
// the placement that goes with it.
//
// Ported as a **shared** Atlas component because it is shared in the donor:
// every `Select` on a migrated screen draws it, and the `Autocomplete`'s popup
// indicator draws the same glyph in its own button
// (`docs/agents/migration-guidelines.md`, "Shared donor components port as
// shared Atlas components"). The donor theme registers no `MuiSelect`,
// `MuiNativeSelect` or `MuiSvgIcon` override — its whole `components` block is
// eight entries, none of them these (`react-ui/src/theme/theme.ts:297-393`) —
// so **MUI's own defaults are the source spec**.
//
// ## Two exports, because the donor has two arrangements
//
// `.MuiSelect-icon` positions *itself* against the field; the `Autocomplete`'s
// indicator is a real 28×28 `IconButton` that positions itself and holds a
// glyph that does not. Same triangle either way, so the glyph is its own
// component and `MuiSelectCaret` is that glyph plus the placement. Folding the
// two together would mean a caret whose position depends on a boolean prop,
// which is the shape that invites a call site to pass the wrong one.
//
// The glyph is a *filled* triangle — `ArrowDropDownIcon`'s single path — and
// the outlined variant insets it `right: 7px` (`NativeSelectInput.js:145-148`;
// the base rule at `:119` is `right: 0`). Both halves are load-bearing: the right
// glyph at the wrong inset sits 8px adrift of the donor's, which is what was
// measured in the Mark-core dialog (#490, parity break B6 from #458,
// owner-ruled D5).
//
// ## What this is *not* for: the date field
//
// #490 asked whether `calendar.tsx`'s trigger belongs here too. It does not.
// The donor's date field is `@mui/x-date-pickers`' `DatePicker`
// (`DatePickerField.tsx:2,32`), whose open affordance is an `IconButton`
// holding a calendar glyph in an `InputAdornment` — not a `Select`. So
// `.MuiSelect-icon`'s `right: 7px` never applies to it, and it draws no caret
// at all. Putting one there would be a divergence, not a fix; the exclusion
// is deliberate and this is where #490's source check is recorded.

/**
 * The ink, and why it is not the donor's.
 *
 * The donor paints `palette.action.active` — `rgba(0, 0, 0, 0.54)`
 * (`NativeSelectInput.js:124`, and no `palette.action` override in the donor
 * theme). Atlas draws the page foreground under `opacity-60`, and the **owner
 * ruled that stands** for all three carets (#579 decision round, **D-D**,
 * 2026-09-21: "Atlas implementation approved. No change."). It is pinned by a
 * case in `mui-select-caret.layout.browser.test.tsx` so a later parity pass
 * cannot quietly drag it back.
 */
const CARET_INK = "text-foreground opacity-60";

/**
 * The rotation, and why it animates.
 *
 * `.MuiSelect-iconOpen` is a bare `transform: rotate(180deg)`
 * (`NativeSelectInput.js:128-136`) and the donor **snaps** — the only
 * transition on the node is `fill 200ms`, inherited from `MuiSvgIcon-root`
 * (`SvgIcon.js:48-50`). Atlas eases it over 150ms, which the same decision
 * round ruled approved (#579, **D-C**).
 */
const CARET_TURN = "transition-transform";

interface MuiCaretProps {
  /**
   * Turns the glyph over, the way `.MuiSelect-iconOpen` does.
   *
   * Optional, because not every trigger has the open state in hand as a value.
   * A Radix select only stamps it as `data-state="open"` on the trigger, and
   * there the rotation rides `MUI_SELECT_CARET_TURN_ON_OPEN` instead — which
   * is one source of truth rather than a prop that could disagree with the
   * attribute beside it.
   */
  open?: boolean;
  className?: string;
}

interface MuiCaretGlyphProps extends MuiCaretProps {
  /**
   * The `data-slot` the glyph answers to.
   *
   * Two, because the donor names them separately — `.MuiSelect-icon` on a
   * select's caret, and a plain glyph inside `.MuiAutocomplete-popupIndicator`
   * on the autocomplete's. Keeping the names apart is what lets a test, or a
   * parity pass, say which of the two arrangements it is looking at.
   */
  slot?: string;
}

/**
 * The glyph alone — 24px, filled, no placement.
 *
 * For the one call site that positions the caret itself: the `Autocomplete`'s
 * popup indicator, which is a real `IconButton` with an accessible name and an
 * unconditional toggle (#588, owner-ruled D-B). There the button carries the
 * 7px inset and this draws inside it.
 */
export function MuiCaretGlyph({ open, className, slot = "mui-caret-glyph" }: MuiCaretGlyphProps) {
  return (
    <svg
      data-slot={slot}
      // `aria-hidden` throughout: the donor's caret is decoration in the
      // `Select` case and, in the `Autocomplete` case, sits inside a button
      // that already carries the name.
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      // `SvgIcon`'s `fontSize: medium` is `pxToRem(24)` against
      // `width/height: 1em` (`SvgIcon.js:44-45,78`) — a 24px box.
      className={cn("size-6 shrink-0", CARET_INK, CARET_TURN, open && "rotate-180", className)}
    >
      {/* `fill: currentColor` and no stroke (`SvgIcon.js:56`) — the filled
          triangle. lucide's chevron is the inverse and is what this replaces. */}
      <path d={ARROW_DROP_DOWN_PATH} fill="currentColor" />
    </svg>
  );
}

/**
 * The caret as a `Select` wears it: the glyph, absolutely placed against the
 * field.
 *
 * The trigger it sits in needs `relative` and enough right padding to keep a
 * long value from running under it — MUI reserves **32px**
 * (`.MuiSelect-select`'s `padding-right`, measured on the donor's three
 * catalog selects), which is the 24px glyph plus its 7px inset plus a pixel.
 */
export function MuiSelectCaret({ open, className }: MuiCaretProps) {
  return (
    <MuiCaretGlyph
      open={open}
      slot="mui-select-caret"
      className={cn(
        // `position: absolute; right: 7` — the outlined variant's rule
        // (`NativeSelectInput.js:118,143-148`), and `Select`'s default variant
        // is `outlined` (`Select.js:76`), so 7px is what the donor measures.
        //
        // **Less the frame's own border**, because `right` resolves against
        // the containing block's *padding* box. MUI's frame has none to
        // subtract: `.MuiInputBase-root` is `border-width: 0` and the visible
        // 1px outline is a child `<fieldset>` (measured on the running donor),
        // so its padding box and border box coincide and `right: 7px` lands
        // 7px from the edge you can see. Several Atlas triggers draw that
        // outline as a real `border` on themselves, and there the same 7px
        // measured 8px. The field shape publishes that border's width as
        // `--mui-field-frame-border` (`mui-field-shape.ts`,
        // `MUI_FIELD_FRAME_BORDER_PUBLISHED`), so the 7px above stays the
        // donor's number rather than becoming a 6 nobody can source. Unset —
        // `0px` — on a frame-owned trigger.
        "pointer-events-none absolute right-[calc(7px-var(--mui-field-frame-border,0px))]",
        // `top: calc(50% - .5em)` (`:121`) — half the glyph's own 24px box, so
        // it stays centred at the 40px small field and the 56px default alike.
        // Spelled as a `top` rather than a `-translate-y-1/2` deliberately: a
        // centring transform would compose with the open state's rotation and
        // there would be no way to assert either one on its own.
        "top-[calc(50%-12px)]",
        className,
      )}
    />
  );
}

/**
 * The right padding a trigger owes the absolutely-placed caret.
 *
 * Exported rather than written out at each of the five call sites, because it
 * is the same measurement as the caret's own inset and the two have to move
 * together: `.MuiSelect-select` is `padding-right: 32px` against a glyph whose
 * left edge is 31px from the field's right.
 *
 * **32px from the edge a user sees**, so less the frame's own border where the
 * trigger draws one — the same correction as the caret's 7px, from the same
 * variable. A plain `pr-8` put the value's right edge 33px in on every
 * self-outlined trigger (#576, item G). `0px` on a frame-owned trigger, whose
 * padding already starts at the visible edge.
 */
export const MUI_SELECT_CARET_GUTTER = "pr-[calc(32px-var(--mui-field-frame-border,0px))]";

/**
 * The class that makes a trigger a positioning context for the caret.
 *
 * Just the positioning. Whether the trigger draws its own outline — and so
 * whether the caret's inset and the gutter take a pixel off — is published by
 * the outline itself: every self-outlined trigger wears `MUI_FIELD_TRIGGER*`,
 * which sets `--mui-field-frame-border` beside its `border`. It used to be a
 * second, `_BORDERED` anchor each call site had to pick; a trigger that picked
 * wrong drew the caret a pixel adrift, and the text insets had no such switch
 * at all.
 */
export const MUI_SELECT_CARET_ANCHOR = "relative";

/**
 * The disabled ink, as a variant the *trigger* carries.
 *
 * `.MuiSelect-icon` is `&.Mui-disabled { color: action.disabled }`
 * (`NativeSelectInput.js:125-127`) — the donor's caret fades with its field.
 * The glyph sets its own colour on itself, so the field's disabled ink cannot
 * reach it; the trigger has to state it. Both spellings are here because the
 * five call sites disable two different ways — a real `disabled` attribute on
 * a button, and Radix's `data-disabled` on a trigger.
 *
 * The ink is MUI's own `rgba(0, 0, 0, 0.26)`, which is already
 * `--color-lennar-action-disabled`; the app's `ink-disabled` is `0.38` and is
 * a different token for a different job. #579's **D-D** approved Atlas's ink
 * for the caret's *resting* state and says nothing about this one, and
 * extending a ruling by analogy is an agent deciding a parity break.
 *
 * `opacity-100` is not redundant: `CARET_INK` carries `opacity-60`, which a
 * colour change does not disturb, so the colour alone composes to
 * `0.26 × 0.6 = 0.156` — `#d7d7d7` against the donor's `#bdbdbd`. MUI never
 * declares an opacity on the icon, so its whole alpha is in the colour and
 * the multiplier has to come off with it.
 *
 * One string literal rather than a joined array, because a module that
 * exports components may only also export *constants*, and `react-refresh`
 * counts a `.join()` as a computed value rather than one.
 */
export const MUI_SELECT_CARET_DISABLED =
  "disabled:[&_[data-slot=mui-select-caret]]:text-lennar-action-disabled disabled:[&_[data-slot=mui-select-caret]]:opacity-100 data-[disabled]:[&_[data-slot=mui-select-caret]]:text-lennar-action-disabled data-[disabled]:[&_[data-slot=mui-select-caret]]:opacity-100";

/**
 * The open-state rotation, for a trigger that owns the state as an attribute
 * rather than as a value.
 *
 * Radix stamps `data-state="open"` on a select's trigger, so the caret inside
 * it turns over from there instead of from an `open` prop. Written out in full
 * rather than composed: Tailwind v4 scans source text, so a variant built by
 * interpolating a selector onto a class name generates no CSS at all.
 */
export const MUI_SELECT_CARET_TURN_ON_OPEN =
  "data-[state=open]:[&_[data-slot=mui-select-caret]]:rotate-180";
